import { useState, useCallback } from "react";
import { giftcardReportService } from "../../services";

export const useGiftcardReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  /**
   * Récupère le résumé des cartes cadeaux
   * @param {Object} params - Paramètres de filtrage
   */
  const getSummary = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await giftcardReportService.getSummary(params);
      setSummary(result.data);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupère la liste détaillée des cartes cadeaux
   * @param {Object} params - Paramètres incluant page, limit, filtres
   */
  const getList = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await giftcardReportService.getList(params);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupère l'historique des utilisations de cartes cadeaux
   * @param {Object} params - Paramètres de filtrage
   */
  const getUsage = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await giftcardReportService.getUsage(params);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupère les détails d'une carte cadeaux spécifique
   * @param {number} cardId - ID de la carte
   */
  const getCardDetails = useCallback(async (cardId) => {
    setLoading(true);
    setError(null);
    try {
      // Note: Cette route n'existe pas encore dans l'API
      // À implémenter si nécessaire
      const result = await giftcardReportService.getCardDetails(cardId);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Calcule les statistiques d'utilisation
   */
  const calculateUsageStats = useCallback((data) => {
    if (!data) return null;

    const totalValue = data.overview?.totalValue || 0;
    const usedValue = data.overview?.usedValue || 0;
    const unusedValue = totalValue - usedValue;
    const usageRate = totalValue > 0 ? (usedValue / totalValue) * 100 : 0;

    return {
      totalValue,
      usedValue,
      unusedValue,
      usageRate,
      byStatus: data.byStatus || {},
      byService: data.byService || [],
    };
  }, []);

  /**
   * Exporte les données des cartes cadeaux
   */
  const exportData = useCallback(async (format = "csv", params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await giftcardReportService.export(format, params);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    summary,
    getSummary,
    getList,
    getUsage,
    getCardDetails,
    calculateUsageStats,
    exportData,
  };
};
